import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Button, Input } from '@arabquiz/ui'
import {
  ChevronLeft,
  ChevronRight,
  FolderCog,
  Library,
  Plus,
  Search,
} from 'lucide-react'
import { QuestionBankItemCard } from './QuestionBankItemCard'
import { ConfirmDialog } from './ConfirmDialog'
import {
  BANK_PAGE_SIZE,
  DIFFICULTY_VALUES,
  questionBankKeys,
  type BankCategory,
  type BankItem,
  type BankListResponse,
  type DifficultyFilter,
  type ScopeFilter,
  type ServerFilters,
} from './types'

type QuestionBankBrowserProps = {
  mode: 'manage' | 'picker'
  /** Manage mode. */
  onCreateItem?: () => void
  onEditItem?: (item: BankItem) => void
  onManageCategories?: () => void
  /** Picker mode — current selection (by id) + toggle. */
  selectedIds?: Set<string>
  onToggleSelect?: (item: BankItem) => void
}

const SCOPE_TABS: ScopeFilter[] = ['all', 'public', 'private']

/**
 * The shared search/filter/browse surface for the Question Bank, reused by
 * the management page (`mode="manage"`) and the add-from-bank picker
 * (`mode="picker"`). Owns its own filter state, the items + categories
 * queries, pagination, and — in manage mode — the delete confirm/mutation.
 *
 * Search/category/scope/difficulty/pagination are all server-driven — the
 * list endpoint filters on each of them.
 */
export function QuestionBankBrowser({
  mode,
  onCreateItem,
  onEditItem,
  onManageCategories,
  selectedIds,
  onToggleSelect,
}: QuestionBankBrowserProps) {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [scope, setScope] = useState<ScopeFilter>('all')
  const [difficulty, setDifficulty] = useState<DifficultyFilter>('all')
  const [page, setPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<BankItem | null>(null)

  // Debounce the search box into the actual query input (and reset to the
  // first page) so each keystroke doesn't fire a request.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const serverFilters: ServerFilters = { search, categoryId, scope, difficulty, page }

  const itemsQuery = useQuery({
    queryKey: questionBankKeys.items(serverFilters),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (categoryId) params.set('categoryId', categoryId)
      params.set('scope', scope)
      if (difficulty !== 'all') params.set('difficulty', difficulty)
      params.set('page', String(page))
      params.set('pageSize', String(BANK_PAGE_SIZE))
      const res = await fetch(
        `/api/v1/question-bank/items?${params.toString()}`,
        { credentials: 'include', signal },
      )
      if (!res.ok) {
        throw new Error(`Failed to load bank items: ${res.status}`)
      }
      return (await res.json()) as BankListResponse
    },
  })

  const categoriesQuery = useQuery({
    queryKey: questionBankKeys.categories(),
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`)
      }
      return (await res.json()) as BankCategory[]
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (item: BankItem) => {
      const res = await fetch(
        `/api/v1/question-bank/items/${encodeURIComponent(item.id)}`,
        { method: 'DELETE', credentials: 'include' },
      )
      if (!res.ok) {
        throw new Error(`Failed to delete item: ${res.status}`)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: questionBankKeys.itemsRoot })
      void queryClient.invalidateQueries({ queryKey: questionBankKeys.categories() })
      setPendingDelete(null)
    },
  })

  const visibleItems = itemsQuery.data?.items ?? []

  const total = itemsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / BANK_PAGE_SIZE))
  const hasFilters =
    search.length > 0 || categoryId !== null || scope !== 'all' || difficulty !== 'all'

  function resetToFirstPage() {
    setPage(1)
  }

  return (
    <div>
      {/* Control bar */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search
              className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-brand-400"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('bankSearchPlaceholder')}
              aria-label={t('bankSearchPlaceholder')}
              className="ps-9"
            />
          </div>

          {mode === 'manage' ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onManageCategories}
                className="gap-2"
              >
                <FolderCog className="h-4 w-4" aria-hidden="true" />
                <span>{t('categoryManagerTitle')}</span>
              </Button>
              <Button
                type="button"
                onClick={onCreateItem}
                className="gap-2 bg-gradient-to-bl from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 shadow-md hover:shadow-lg"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span>{t('bankCreateItem')}</span>
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {/* Scope tabs */}
          <div
            role="tablist"
            aria-label={t('bankScopeAll')}
            className="inline-flex rounded-xl border border-brand-200 bg-white p-1"
          >
            {SCOPE_TABS.map((value) => {
              const active = scope === value
              const label =
                value === 'all'
                  ? t('bankScopeAll')
                  : value === 'public'
                    ? t('bankScopePublic')
                    : t('bankScopePrivate')
              return (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setScope(value)
                    resetToFirstPage()
                  }}
                  className={
                    'rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ' +
                    (active
                      ? 'bg-brand-500 text-white'
                      : 'text-brand-700 hover:bg-brand-50')
                  }
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="bank-category-filter" className="sr-only">
              {t('bankCategory')}
            </label>
            <select
              id="bank-category-filter"
              value={categoryId ?? ''}
              onChange={(e) => {
                setCategoryId(e.target.value === '' ? null : e.target.value)
                resetToFirstPage()
              }}
              className="h-10 rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <option value="">{t('bankAllCategories')}</option>
              {(categoriesQuery.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty filter (client-side) */}
          <div className="flex items-center gap-2">
            <label htmlFor="bank-difficulty-filter" className="sr-only">
              {t('bankDifficulty')}
            </label>
            <select
              id="bank-difficulty-filter"
              value={difficulty}
              onChange={(e) => {
                setDifficulty(e.target.value as DifficultyFilter)
                resetToFirstPage()
              }}
              className="h-10 rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <option value="all">{t('bankDifficulty')}</option>
              {DIFFICULTY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value === 'Easy'
                    ? t('bankDifficultyEasy')
                    : value === 'Medium'
                      ? t('bankDifficultyMedium')
                      : t('bankDifficultyHard')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        {itemsQuery.isLoading ? <BrowserSkeleton /> : null}

        {itemsQuery.isError ? (
          <div
            className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {t('bankLoadError')}
          </div>
        ) : null}

        {itemsQuery.isSuccess && visibleItems.length === 0 ? (
          <BankEmptyState
            hasFilters={hasFilters}
            noResultsLabel={t('bankNoResults')}
            emptyTitle={t('bankEmpty')}
            emptyDescription={t('bankEmptyDescription')}
            createLabel={mode === 'manage' ? t('bankCreateItem') : undefined}
            onCreate={mode === 'manage' ? onCreateItem : undefined}
          />
        ) : null}

        {itemsQuery.isSuccess && visibleItems.length > 0 ? (
          <ul
            role="list"
            className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
          >
            {visibleItems.map((item) => (
              <li key={item.id}>
                <QuestionBankItemCard
                  item={item}
                  mode={mode}
                  onEdit={onEditItem}
                  onDelete={(target) => setPendingDelete(target)}
                  selected={selectedIds?.has(item.id) ?? false}
                  onToggleSelect={onToggleSelect}
                />
              </li>
            ))}
          </ul>
        ) : null}

        {itemsQuery.isLoading ? (
          <span className="sr-only" role="status" aria-live="polite">
            {tCommon('loading')}
          </span>
        ) : null}

        {/* Pagination */}
        {itemsQuery.isSuccess && pageCount > 1 ? (
          <nav
            aria-label={tCommon('page')}
            className="mt-8 flex items-center justify-center gap-3"
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label={tCommon('back')}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            </button>
            <span className="text-sm font-semibold text-brand-700 tabular-nums">
              {tCommon('pageOf', { page, total: pageCount })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              aria-label={tCommon('next')}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            </button>
          </nav>
        ) : null}
      </div>

      {mode === 'manage' ? (
        <ConfirmDialog
          open={pendingDelete !== null}
          title={t('bankDeleteItem')}
          message={t('bankDeleteConfirm')}
          confirmPending={deleteMutation.isPending}
          onConfirm={() => {
            if (pendingDelete) deleteMutation.mutate(pendingDelete)
          }}
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
    </div>
  )
}

function BrowserSkeleton() {
  return (
    <ul
      role="list"
      aria-hidden="true"
      className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="h-56 rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-100 ps-5 pe-5 py-5 animate-pulse"
        >
          <div className="h-6 w-24 rounded-full bg-brand-100" />
          <div className="mt-4 h-3.5 w-full rounded bg-brand-50" />
          <div className="mt-2 h-3.5 w-5/6 rounded bg-brand-50" />
          <div className="mt-2 h-3.5 w-2/3 rounded bg-brand-50" />
          <div className="mt-5 h-6 w-20 rounded-full bg-brand-50" />
        </li>
      ))}
    </ul>
  )
}

type BankEmptyStateProps = {
  hasFilters: boolean
  noResultsLabel: string
  emptyTitle: string
  emptyDescription: string
  createLabel?: string | undefined
  onCreate?: (() => void) | undefined
}

function BankEmptyState({
  hasFilters,
  noResultsLabel,
  emptyTitle,
  emptyDescription,
  createLabel,
  onCreate,
}: BankEmptyStateProps) {
  if (hasFilters) {
    return (
      <div className="rounded-3xl border border-dashed border-brand-200 bg-white/60 px-6 py-16 text-center">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
          aria-hidden="true"
        >
          <Search className="h-8 w-8 text-brand-600" strokeWidth={1.5} />
        </div>
        <p className="text-brand-700 text-lg font-semibold">{noResultsLabel}</p>
      </div>
    )
  }

  return (
    <div className="rounded-3xl border border-dashed border-brand-200 bg-white/60 px-6 py-16 text-center">
      <div
        className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
        aria-hidden="true"
      >
        <Library className="h-10 w-10 text-brand-600" strokeWidth={1.5} />
      </div>
      <h2 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-2">
        {emptyTitle}
      </h2>
      <p className="text-brand-700 mb-6 max-w-md mx-auto leading-relaxed">
        {emptyDescription}
      </p>
      {createLabel && onCreate ? (
        <Button type="button" size="lg" onClick={onCreate} className="gap-2">
          <Plus className="h-5 w-5" aria-hidden="true" />
          <span>{createLabel}</span>
        </Button>
      ) : null}
    </div>
  )
}
